import Server from "../server";
import View from "../framework/view";
import {OptionSelected, Order} from "../bo/order";
import template from "../../pugc/view-order";
import {
    canEditOrder, canViewOrderSession,
    customFieldsSorter,
    NOTE_TO_FLORIST,
    Role,
    ROLE_CASUAL_DELIVERY,
    ROLE_DELIVERY,
    viewOrderDetails
} from "../misc/types";
import {formToJson, renderDollars, renderTimestamp} from "../misc/utils";
import jQuery from "jquery";
import {OrderHistory} from "../bo/order_history";
import {
    CardNotification,
    EditOrderNotification,
    InvoiceNotification,
    MapOrdersNotification,
    PrintOrdersNotification
} from "./wrb";
import {App} from "../app/app";
import {calcLineItemExtras, getDeliveryCost, getDirectionsUrl, hasExtras, isDelivery, isToPay} from "../misc/orders";
import {addDelivery, getDeliveries, removeDelivery} from "../misc/delivery";
import Alerts from "../misc/alerts";
import {toJSONDate} from "../misc/dates";
import {User} from "../bo/user";
import {renderName, sizeRe} from "../shop/product";

export class ViewOrderView extends View {

    static classname = "view-order";

    private readonly server = Server.instance();

    private id : string;

    private order : Order | undefined;

    private app: App;

    private buyer_phone : string | undefined;
    private delivery_phone : string | undefined;

    private buyer_name : string | undefined;
    private delivery_name : string | undefined;

    private buyer_message : string | undefined;
    private delivery_message : string | undefined;

    private baseUrl?: string;

    private editingHistory: boolean = false;

    private casualDrivers: User[] = [];

    private loadedIds : string[] | undefined;

    private deliveryAddressLog: string[] | undefined;

    private sendSmsMarkDelivered = false;

    constructor(app: App, id: string) {
        super(ViewOrderView.classname);
        this.app = app;
        this.id = id;
        this.refresh();
        this.server.runtimeParams_.then((params) => {
            this.baseUrl = params["wrb.client.short_url"];
            this.update();
        });

        const user = this.app.getUser();
        if (user && user.admin) {
            this.server.getUsers().then(users => {
                this.casualDrivers = users.filter(user => user.role === ROLE_CASUAL_DELIVERY);
                if (this.casualDrivers.length > 0) {
                    this.update();
                }
            });
        }
    }

    async refresh() {

        this.loadedIds = await getDeliveries();

        this.order = await this.server.getOrder(this.id);

        // @ts-ignore
        const address = this.order?.shippingInfo?.shipmentDetails?.address;

        // @ts-ignore
        const deliveryPhone = address?.phone || undefined;
        // @ts-ignore
        const buyerPhone = this.order.buyerInfo.phone || undefined;

        this.buyer_phone = buyerPhone;
        this.delivery_phone = deliveryPhone;

        this.buyer_name = [ this.order?.buyerInfo?.firstName, this.order?.buyerInfo?.lastName ].filter(x => x !== undefined).join(" ");
        this.delivery_name = [ address?.fullName?.firstName, address?.fullName?.lastName ].filter(x => x !== undefined).join(" ");

        if (isDelivery(this.order)) {
            this.buyer_message = "Your flowers from West Row Botanica Boutique Florist have been delivered. Thank you for using our service.";
            this.delivery_message = "Your flowers from West Row Botanica Boutique Florist have been delivered. Enjoy your flowers!\n\nFlower care tips: https://www.westrowbotanica.com.au/care";
        } else {
            this.buyer_message = "Your order from West Row Botanica is ready for pickup.";
        }

        this.update();
    }

    protected render0(): JQuery<HTMLElement> {

        const user = this.app.getUser();
        const viewDetails = user && viewOrderDetails(user.role as Role | undefined);
        const canEdit = user && canEditOrder(user.role as Role | undefined);
        const canViewSession = user && canViewOrderSession(user.role as Role | undefined);
        const loadedIds = getDeliveries();
        const isLoaded = this.order && this.order.id && this.loadedIds && this.loadedIds.indexOf(this.order.id) != -1;

        const directionsUrl = getDirectionsUrl(this.order);

        const toPay = isToPay(this.order);

        const deliveryCost = this.order ? getDeliveryCost(this.order) : undefined;

        const isSizeOption = (o: OptionSelected) => {
            return o.option?.match(sizeRe);
        };

        const data = {
            order: this.order,
            customFieldsSorter: customFieldsSorter,
            NOTE_TO_FLORIST: NOTE_TO_FLORIST,
            renderTimestamp: renderTimestamp,
            viewDetails: viewDetails,
            isDelivery: isDelivery,
            buyer_phone: this.buyer_phone,
            delivery_phone: this.delivery_phone,
            buyer_name: this.buyer_name,
            delivery_name: this.delivery_name,
            buyer_message: this.buyer_message,
            delivery_message: this.delivery_message,
            isLoaded: isLoaded,
            canEdit: canEdit,
            canViewSession: canViewSession,
            directionsUrl: directionsUrl,
            toPay: toPay,
            editingHistory: this.editingHistory,
            renderDollars: renderDollars,
            deliveryCost: deliveryCost,
            isCasualDriver: user && user.role === ROLE_CASUAL_DELIVERY,
            casualDrivers: this.casualDrivers,
            deliveryAddressLog: this.deliveryAddressLog,
            renderName: renderName,
            isSizeOption: isSizeOption
        };

        const out = this.pug(template, data);

        return out;
    }

    async recordHistory(status: string, ignoreSms?: boolean) {

        const current = this.order?.currentStatus;
        let noUpdateOrderStatus = false;
        if (! status) {
            status = this.elt().find("input[name='other'").val() as string;
            if (current == "New") {
                noUpdateOrderStatus = true;
            }
        }

        if (! status) {
            return;
        }

        if (status == "Delivered" && hasExtras(this.order)) {
            const ok = await Alerts.confirmation({
                message: "This order has extras",
                options: [ "Ok" ]
            });
        }

        if (status == "Delivered" && ! ignoreSms) {
            const smsHistory = (this.order?.orderHistory || []).filter(h => h.newStatus?.match(/SMS to/));

            if (smsHistory.length == 0) {
                this.prepareSendSMS(true);
                return;
            }

        }

        const history : OrderHistory = {
            newStatus: status
        };

        if (noUpdateOrderStatus) {
            // @ts-ignore
            history.noUpdateOrderStatus = true;
        }

        if (status == "Out for delivery" ) {
            await this.deliveryLoad(true, true);
        }

        this.server.recordStatus(this.id, history).then((order) => {
            this.order = order;
            this.update();
        });
    }

    async giveToDriver(driver: string) {
        if (! this.order || ! this.order.id) {
            return;
        }
        await this.server.giveToDriver(this.order.id, driver);
        this.refresh();
    }

    async print() {
        if (this.order && ! this.order.deliveryDate) {
            const result = await Alerts.alert({
                message: "Order date not set. Set the order date before printing!"
            });
        } else {
            this.notify(new PrintOrdersNotification([ this.id ]));
        }
    }

    async card() {
        this.notify(new CardNotification([ this.id ]));
    }

    map() {
        this.notify(new MapOrdersNotification([ this.id ]));
    }

    invoice() {
        this.notify(new InvoiceNotification(this.id));
    }

    edit() {
        this.notify(new EditOrderNotification(this.id));
    }

    prepareSendSMS(markDelivered: boolean) {
        this.elt().find(".sms-modal.js-modal .send-button").text(markDelivered ? "Send and mark delivered" : "Send");
        this.sendSmsMarkDelivered = markDelivered;
        this.elt().find(".sms-modal.js-modal").addClass("active");
    }

    preparePhone() {
        this.elt().find(".phone-modal.js-modal").addClass("active");
    }

    async sendSMS() {

        if (this.order == null || ! this.order.id) {
            return;
        }

        const form = this.elt().find(".send-sms form");
        const data : any = {};

        formToJson(form, data);

        const buyer_message = data.buyer_message as string;
        const delivery_message = data.delivery_message as string;

        const buyer_phone = data.buyer_phone as string;
        const delivery_phone = data.delivery_phone as string;

        const messages = [
            {
                phone: buyer_phone,
                message: buyer_message
            },
            {
                phone: delivery_phone,
                message: delivery_message
            }
            ].filter((m) => m.phone && m.message);

        this.order = await this.server.sendSMS(this?.order?.id as string, messages);

        if (this.sendSmsMarkDelivered) {
            this.sendSmsMarkDelivered = false;
            await this.recordHistory("Delivered", true);
        }

        this.update();
    }

    async deliveryLoad(status: boolean, noSideEffects?: boolean, forceDate?: boolean, forceExtras?: boolean) {

        const today = toJSONDate(new Date());
        const deliveryDate = this.order?.deliveryDate;
        const YES = "Yes, add to load";

        if (status && ! noSideEffects && deliveryDate && deliveryDate != today && ! forceDate) {
            const ok = await Alerts.confirmation({
                message: `Delivery date for this order is ${deliveryDate}, really add to load?`,
                options: [ YES, "No" ]
            });
            if (ok === YES) {
                await this.deliveryLoad(status, noSideEffects, true, forceExtras);
            }
            return;
        }

        const extraAlerts = [];

        let items = 0;
        const lines = this.order?.lineItems || [];
        for (const line of lines) {
           items += +(line.quantity || 1);
        }

        if (status && ! noSideEffects && items > 1 && ! forceExtras) {
            extraAlerts.push(`This order has multiple (${items}) items`);
        }

        if (status && ! noSideEffects && hasExtras(this.order) && ! forceExtras) {
            extraAlerts.push("This order has extras");
        }

        if (status && ! noSideEffects && ! forceExtras) {
            for (const line of lines) {
                const e = calcLineItemExtras(line);
                if (e.vase && ! e.box) {
                    extraAlerts.push("This order should include a vase");
                }
            }
        }

        if (extraAlerts.length > 0) {
            const ok = await Alerts.confirmation({
                messageHTML: extraAlerts.join(". <br><br>") + ".",
                options: ["Ok"]
            });
            await this.deliveryLoad(status, noSideEffects, forceDate, true);
            return;
        }

        if (this?.order?.id) {
            if (status) {
                await addDelivery(this?.order?.id);
            } else {
                await removeDelivery(this?.order?.id);
            }
        }

        if (status && ! noSideEffects && this?.order?.currentStatus != "Delivered" ) {
            await this.recordHistory("Out for delivery");
        }

        if (! status && ! noSideEffects && this?.order?.currentStatus == "Out for delivery") {
            const history = this?.order?.orderHistory || [];
            // Find previous status before Out for delivery
            await this.recordHistory("Removed from load");
        }

        this.loadedIds = await getDeliveries();

        this.update();
    }

    prepareUploadPhoto(type: "camera" | "image") {
        const input = this.elt().find(".photo-modal.js-modal input.photo");
        switch (type) {
            case "camera":
                input.attr("capture", "environment");
                break;
            case "image":
                input.removeAttr("capture");
                break;
        }
        input.addClass("active");
        this.elt().find(".photo-modal.js-modal").addClass("active");
        this.elt().find(".photo-modal.js-modal input").trigger("click");
    }

    uploadPhoto() {

        if (! this.order) {
            return;
        }

        const input = this.elt().find("input.photo") as JQuery<HTMLInputElement>;

        const file = input[0].files && input[0].files.length && input[0].files[0];

        if (! file) {
            return;
        }

        const upload = this.server.uploadFile(file);

        const history : OrderHistory = {
            newStatus: this.order.currentStatus
        }

        const baseurl = this.server.getBaseUrl();

        upload.then((data: any) => {

            history.imageUrl = baseurl + "/api/docs/" + data.hash;

            this.server.recordStatus(this.id, history).then((order) => {
                this.order = order;
                this.update();
            });

        });
    }

    lightboxImg(url: string) {
        this.elt().find(".image-modal img").attr("src", url);
        this.elt().find(".image-modal.js-modal").addClass("active");
    }

    copyImageUrl() {
        if (! this.order || ! this.baseUrl) {
            return;
        }

        const baseUrl = this.baseUrl;
        const url = `${baseUrl}#/img/${this.order.qrcode_id}`;

        if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(function() {
                Alerts.alert(url + " copied to clipboard");
            }, function(err) {
                Alerts.alert(url);
            });
        } else {
            Alerts.alert(url);
        }
    }

    editHistory(edit: boolean) {
        this.editingHistory = edit;
        this.update(".history");
    }

    deleteHistory(idx: number) {
        if (! this.order || ! this.order.id) {
            return;
        }
        const del_ = this.server.deleteHistory(this.order.id, idx);
        del_.then((order) => {
            this.order = order;
            this.update();
        });
    }

    setImportant(important: boolean) {
        if (! this.order || ! this.order.id) {
            return;
        }
        const f_ = this.server.setImportant(this.order.id, important);
        f_.then((order) => {
            this.order = order;
            this.update();
        });

    }

    showDeliveryAddressLog() {
        if (this.order) {
            // @ts-ignore
            this.deliveryAddressLog = this.order.deliveryAddressLog;
            this.update(".deliveryAddressLog");
        }
    }

}

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".action",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        const photo = jQuery(elt).data("photo") as undefined | "camera" | "image";
        const important = jQuery(elt).data("important");
        const driver = jQuery(elt).data("driver") as string;
        if (jQuery(elt).data("sms")) {
            that.prepareSendSMS(false);
        } else if (jQuery(elt).data("phone")) {
            that.preparePhone();
        } else if (photo) {
            that.prepareUploadPhoto(photo);
        } else if (important !== undefined) {
            that.setImportant(!!important);
        } else if (driver) {
            that.giveToDriver(driver);
        } else {
            const status = jQuery(elt).data("status") as string;
            that.recordHistory(status);
        }
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".delivery-load",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        const status = jQuery(elt).data("status") as boolean;
        that.deliveryLoad(status);
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".edit",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        that.edit();
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".print",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        that.print();
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".card",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        that.card();
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".map",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        that.map();
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".invoice",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        that.invoice();
    });

View.on(
    "." + ViewOrderView.classname,
    "submit",
    ".send-sms form",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        that.sendSMS();
    });

View.on(
    "." + ViewOrderView.classname,
    "change",
    ".attach-photo input",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        that.uploadPhoto();
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".lightboxImg",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        const url = jQuery(elt).attr("src") as string;
        that.lightboxImg(url);
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".edit-history",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        that.editHistory(true);
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".view-history",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        that.editHistory(false);
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".delete-history",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        const idx = jQuery(elt).data("idx") as number;
        that.deleteHistory(idx);
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".copy-image-url",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        const url = jQuery(elt).attr("src") as string;
        that.copyImageUrl();
    });

View.on(
    "." + ViewOrderView.classname,
    "click",
    ".show-deliveryAddressLog",
    function(that: ViewOrderView, event, elt) {
        event.preventDefault();
        that.showDeliveryAddressLog();
    });
